import Dexie, { type EntityTable } from 'dexie'
import { migrateProject } from './migration'
import {
  approximateProjectBytes,
  retainedRecoverySnapshots,
  shouldCreateRecoverySnapshot,
  shouldOfferPendingSave,
  type PendingProjectSave,
  type RecoveryCandidate,
  type RecoveryReason,
  type RecoverySnapshot,
} from './recovery'
import type { DeckProject } from './types'
import { newId } from './utils'

class DeckelierDatabase extends Dexie {
  projects!: EntityTable<DeckProject, 'id'>
  recoverySnapshots!: EntityTable<RecoverySnapshot, 'id'>
  pendingSaves!: EntityTable<PendingProjectSave, 'projectId'>

  constructor() {
    super('deckelier')
    this.version(1).stores({ projects: 'id, name, updatedAt' })
    this.version(2).stores({ projects: 'id, name, updatedAt' })
    this.version(3).stores({ projects: 'id, name, updatedAt' })
    this.version(4).stores({
      projects: 'id, name, updatedAt',
      recoverySnapshots: 'id, projectId, createdAt, [projectId+createdAt]',
      pendingSaves: 'projectId, startedAt',
    })
  }
}

export interface ProjectLibraryState {
  projects: DeckProject[]
  recoveryCandidates: RecoveryCandidate[]
  damagedProjects: DamagedProjectSummary[]
}

export interface DamagedProjectSummary {
  id: string
  name: string
  updatedAt?: string
  reason: string
}

export interface SaveProjectResult {
  snapshotStored: boolean
}

export const database = new DeckelierDatabase()

const migrateStoredProject = (project: DeckProject) => migrateProject(project)

const latestProjectSnapshot = async (projectId: string) => {
  const records = await database.recoverySnapshots.where('projectId').equals(projectId).toArray()
  return records.sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))[0]
}

const pruneProjectSnapshots = async (projectId: string, projectBytes: number) => {
  const records = await database.recoverySnapshots.where('projectId').equals(projectId).toArray()
  const keep = new Set(retainedRecoverySnapshots(records, projectBytes).map((snapshot) => snapshot.id))
  const expiredIds = records.filter((snapshot) => !keep.has(snapshot.id)).map((snapshot) => snapshot.id)
  if (expiredIds.length) await database.recoverySnapshots.bulkDelete(expiredIds)
}

export const createRecoverySnapshot = async (
  project: DeckProject,
  reason: RecoveryReason = 'manual',
  force = reason === 'manual',
) => {
  const migrated = migrateStoredProject(project)
  const latest = await latestProjectSnapshot(migrated.id)
  if (!force && !shouldCreateRecoverySnapshot(latest, migrated)) return null
  const snapshot: RecoverySnapshot = {
    id: newId('recovery'),
    projectId: migrated.id,
    projectName: migrated.name,
    projectUpdatedAt: migrated.updatedAt,
    createdAt: new Date().toISOString(),
    reason,
    byteSize: approximateProjectBytes(migrated),
    project: migrated,
  }
  await database.recoverySnapshots.put(snapshot)
  await pruneProjectSnapshots(migrated.id, snapshot.byteSize)
  return snapshot
}

export const listRecoverySnapshots = async () => {
  const records = await database.recoverySnapshots.orderBy('createdAt').reverse().toArray()
  const valid: RecoverySnapshot[] = []
  for (const record of records) {
    try {
      const project = migrateStoredProject(record.project)
      valid.push({ ...record, project, projectName: project.name, projectUpdatedAt: project.updatedAt })
    } catch {
      // A damaged recovery copy must not prevent the rest of the library opening.
    }
  }
  return valid
}

export const countRecoverySnapshots = () => database.recoverySnapshots.count()

export const deleteRecoverySnapshot = (id: string) => database.recoverySnapshots.delete(id)

export const dismissPendingSave = (projectId: string) => database.pendingSaves.delete(projectId)

export const loadProjectLibrary = async (): Promise<ProjectLibraryState> => {
  const rawProjects = await database.projects.orderBy('updatedAt').reverse().toArray()
  const projects: DeckProject[] = []
  const projectById = new Map<string, DeckProject>()
  const damagedProjectIds = new Set<string>()
  const damagedProjects: DamagedProjectSummary[] = []
  for (const rawProject of rawProjects) {
    try {
      const project = migrateStoredProject(rawProject)
      projects.push(project)
      projectById.set(project.id, project)
    } catch (error) {
      damagedProjectIds.add(rawProject.id)
      damagedProjects.push({
        id: rawProject.id,
        name: typeof rawProject.name === 'string' && rawProject.name.trim() ? rawProject.name : 'Unreadable project',
        updatedAt: typeof rawProject.updatedAt === 'string' ? rawProject.updatedAt : undefined,
        reason: error instanceof Error ? error.message : 'The saved project record is damaged.',
      })
    }
  }

  const recoveryCandidates: RecoveryCandidate[] = []
  const stalePendingIds: string[] = []
  for (const record of await database.pendingSaves.toArray()) {
    try {
      const project = migrateStoredProject(record.project)
      const pending = {
        ...record,
        project,
        projectName: project.name,
        projectUpdatedAt: project.updatedAt,
      }
      const savedProject = projectById.get(pending.projectId) ?? null
      const savedProjectDamaged = damagedProjectIds.has(pending.projectId)
      if (shouldOfferPendingSave(pending, savedProject, savedProjectDamaged)) {
        recoveryCandidates.push({ pending, savedProject, savedProjectDamaged })
      } else {
        stalePendingIds.push(pending.projectId)
      }
    } catch {
      // Keep an unreadable pending record untouched so repair tooling can inspect it later.
    }
  }
  if (stalePendingIds.length) await database.pendingSaves.bulkDelete(stalePendingIds)

  recoveryCandidates.sort((left, right) => Date.parse(right.pending.startedAt) - Date.parse(left.pending.startedAt))
  return { projects, recoveryCandidates, damagedProjects }
}

export const listProjects = async () => (await loadProjectLibrary()).projects

export const getRawProjectRecord = (id: string) => database.projects.get(id) as Promise<unknown>

export const removeDamagedProjectRecord = (id: string) => database.projects.delete(id)

export const saveProject = async (
  project: DeckProject,
  reason: RecoveryReason = 'autosave',
): Promise<SaveProjectResult> => {
  const migrated = migrateStoredProject(project)
  let snapshotStored = false
  try {
    const previousRecord = await database.projects.get(migrated.id)
    if (previousRecord) {
      const previous = migrateStoredProject(previousRecord)
      if (previous.updatedAt !== migrated.updatedAt) {
        snapshotStored = Boolean(await createRecoverySnapshot(previous, reason, false))
      }
    }
  } catch {
    // A historical copy is valuable but must not block the current save.
  }

  const pending: PendingProjectSave = {
    projectId: migrated.id,
    projectName: migrated.name,
    projectUpdatedAt: migrated.updatedAt,
    startedAt: new Date().toISOString(),
    byteSize: approximateProjectBytes(migrated),
    project: migrated,
  }

  // These writes are deliberately separate. If the main project write is interrupted,
  // the pending record survives as the exact revision Deckelier was trying to save.
  await database.pendingSaves.put(pending)
  await database.projects.put(migrated)
  try {
    await database.pendingSaves.delete(migrated.id)
  } catch {
    // The project itself is safely stored. A stale marker is cleared on the next launch.
  }
  return { snapshotStored }
}

export const restoreRecoveryProject = async (project: DeckProject) => {
  const restored = migrateStoredProject(structuredClone(project))
  try {
    const current = await database.projects.get(restored.id)
    if (current) await createRecoverySnapshot(migrateStoredProject(current), 'manual', true)
  } catch {
    // Restoring a healthy recovery copy is still preferable if the current record is damaged.
  }
  restored.updatedAt = new Date().toISOString()
  await saveProject(restored, 'manual')
  await dismissPendingSave(restored.id)
  return restored
}

export const removeProject = async (id: string) => {
  const snapshotIds = await database.recoverySnapshots.where('projectId').equals(id).primaryKeys()
  await database.transaction('rw', database.projects, database.recoverySnapshots, database.pendingSaves, async () => {
    await database.projects.delete(id)
    if (snapshotIds.length) await database.recoverySnapshots.bulkDelete(snapshotIds)
    await database.pendingSaves.delete(id)
  })
}
